import axios, { AxiosError } from 'axios';
import getConfig from '@src/util/getConfig';
import { getSessionToken, isSessionTokenExpired } from '@descope/react-sdk';
import * as Sentry from '@sentry/react';
import { useSessionStore } from '@src/store';
import toast from 'react-hot-toast';
import { axiosDefaults } from '@src/apiFetch/axiosConfig.const';
import { trackEvent } from '@src/utils/trackEvent';
import { logError } from '@src/utils/logEvent';
import { activateMaintenanceMode, isMaintenanceError } from '@src/store/maintenanceState';

const API_PATH = '/api/xano';

// GATSBY_API_ORIGIN (path ignored) wins, else the page's host on GATSBY_API_PORT, else the page's own origin (production).
const resolveBaseUrl = () => {
    // No window during SSR (gatsby build), and no requests are made there.
    if (typeof window === 'undefined') {
        return API_PATH;
    }
    try {
        return new URL(API_PATH, process.env.GATSBY_API_ORIGIN).href;
    } catch {
        const url = new URL(API_PATH, window.location.origin);
        url.port = process.env.GATSBY_API_PORT || '';
        return url.href;
    }
};

/**
 * @type {AxiosInstance}
 */
const BACKEND_API = axios.create({
    baseURL: resolveBaseUrl(),
});
Object.assign(BACKEND_API.defaults, axiosDefaults);

// axios may hand back either a plain object or an AxiosHeaders instance
// depending on where in the lifecycle the error was raised.
const readRequestId = (config) => {
    const headers = config?.headers;
    if (!headers) return null;
    if (typeof headers.get === 'function') return headers.get('x-request-id') ?? null;
    return headers['x-request-id'] ?? null;
};

const newRequestId = () => (
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `r-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

BACKEND_API.interceptors.request.use(
    async (config) => {
        // The backend adopts this as its own request id (genRequestId in
        // bootstrap.ts), which is what lets a browser log line be joined to
        // the backend line that served it.
        const headers = { ...config.headers, 'x-request-id': newRequestId() };

        const xanoBranch = getConfig().xano_branch;
        if (xanoBranch) {
            headers['X-Branch'] = xanoBranch;
        }

        return { ...config, headers };
    },
);

let lastToast = 0;
const triggerSessionExpired = () => {
    if (Date.now() - lastToast > 5000) {
        toast.error('Session expired');
        lastToast = Date.now();
    }
    useSessionStore.getState().logout();
};

BACKEND_API.interceptors.request.use(
    async (config) => {
        if (config.skipAuth || config.headers.Authorization) {
            return config;
        }
        let accessToken = getSessionToken();

        if (accessToken && isSessionTokenExpired()) {
            await useSessionStore.getState().refresh();
            accessToken = getSessionToken();
            if (!accessToken || isSessionTokenExpired()) {
                throw new Error('Session refresh failed');
            }
        }

        if (!accessToken) {
            throw new Error('Unable to make secure request, current session does not have a token set.');
        }

        // eslint-disable-next-line no-param-reassign
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
        };

        return config;
    },
    (error) => {
        Sentry.captureException(error);
        console.error('error: ', error);
        trackEvent('session_refresh_failed', {
            error_type: String(error?.response?.status ?? 'unknown'),
        });
        setTimeout(triggerSessionExpired, 0); // Next frame
        return Promise.reject(error);
    },
);

BACKEND_API.interceptors.response.use(
    (response) => {
        // A misrouted request lands on the SPA fallback and gets index.html
        // with a 200; fail it instead of handing HTML to the caller.
        const contentType = response.headers?.['content-type'] || '';
        if (contentType.includes('text/html')) {
            const error = new AxiosError(
                `Expected an API response but got HTML: ${response.config?.url}`,
                AxiosError.ERR_BAD_RESPONSE,
                response.config,
                response.request,
                response,
            );
            Sentry.captureException(error);
            return Promise.reject(error);
        }
        return response;
    },
    (error) => {
        // Backend is soft-frozen (maintenance mode) — flag this tab (and broadcast
        // to all other open tabs) before any session-expired handling;
        if (isMaintenanceError(error)) {
            activateMaintenanceMode();
            return Promise.reject(error);
        }
        if (!error.response && error.request) {
            Sentry.captureException(new Error(`Network Error: ${error.config?.url}`), {
                extra: {
                    url: error.config?.url,
                    method: error.config?.method,
                    message: error.message || 'No response received',
                },
            });
        }
        // URL, method and status only — never bodies (see logEvent.ts).
        logError('api_request_failed', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status ?? null,
            request_id: readRequestId(error.config),
            error_message: error.message,
        });

        const { message } = error.response?.data || {};
        if (message === 'Session expired' || message === 'The token expired.') {
            setTimeout(triggerSessionExpired, 0); // Next frame
        }
        return Promise.reject(error);
    },
);

export {
    BACKEND_API,
};
